<template>
  <div class="recorder">
    <el-button
      class="recording"
      :class="{ 'disable-recording': disableRecord }"
      @click="record"
      circle
      :disabled="disableRecord"
    >
      <img :src="recordIcon" />
      <div :class="['tooltip', { trainer: isTrainer }]">
        <span class="tooltip-text" v-html="$t('recordingUnavailable')"></span>
      </div>
    </el-button>
    <p :class="{ 'disabled-text': disableRecord }">{{ recordingText }}</p>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { store } from '@/utils/common';
import { SET_HIDE_MYSTAGE_RECORD_MESSAGE } from '@/graphql/mutations/userSetting';
import { apolloProvider } from '@/modules/myStageGraphQL';

export default {
  props: {
    remoteUsers: {
      type: Array
    }
  },
  data() {
    return {
      recordSuccess: true
    };
  },
  mounted() {
    window.addEventListener('beforeunload', this.closedBrowser);
  },
  computed: {
    ...mapState(['userId', 'lessonId', 'accType']),
    ...mapState('videoRtc', ['localUser']),
    ...mapState('recorder', ['isRecording', 'recorder', 'displayStream']),
    ...mapState('layout', ['layoutColor']),
    ...mapState('userOtherSetting', ['isHideRecordMessage']),
    ...mapGetters(['isTrainer']),
    ...mapGetters('uaParser', ['isSafariBrowser', 'isFirefoxBrowser']),

    recordIcon() {
      if (this.isRecording) {
        return require(`@/assets/img/recording_${this.layoutColor}_stop.svg`);
      }

      if (this.disableRecord) {
        return require(`@/assets/img/recording_disable.svg`);
      }

      return require(`@/assets/img/recording_${this.layoutColor}.svg`);
    },

    recordingText() {
      if (this.isRecording) {
        return this.$t('buttons.recording');
      }
      return this.$t('buttons.record');
    },

    disableRecord() {
      return this.isSafariBrowser;
    },
  },
  methods: {
    ...mapMutations('recorder', ['setDisplayStream', 'setRecording']),
    ...mapMutations('userOtherSetting', ['setIsHideRecordMessage']),
    ...mapActions('recorder', [
      'initRecorder',
      'addStreams',
      'updateAudioStreams',
      'stopRecorder',
      'resetRecorder',
      'startTimer',
      'clearTimer'
    ]),

    record() {
      if (this.isHideRecordMessage || this.isRecording) {
        this.setRecording(!this.isRecording);
      } else {
        this.openRecordDialog();
      }
    },

    openRecordDialog() {
      const h = this.$createElement;
      this.$msgbox({
        title: '',
        message: h('div', null, [
          h('h3', { class: 'record-modal-title' }, this.$t('recordDialog.title')),
          h('p', {
            class: 'record-modal-message',
            domProps: { innerHTML: this.$t('recordDialog.message') }
          }),
          h(
            'el-checkbox',
            {
              attr: {
                checked: this.hideModal
              },
              on: {
                change: this.checkHideModal
              },
              class: 'record-check-hide'
            },
            this.$t('recordDialog.checkLabel')
          )
        ]),
        showCancelButton: true,
        confirmButtonText: this.$t('recordDialog.buttons.confirm'),
        cancelButtonText: this.$t('recordDialog.buttons.cancel'),
        customClass: 'record-modal',
        confirmButtonClass: 'record-modal-btn-confirm',
        cancelButtonClass: 'record-modal-btn-cancel'
      })
      .then(() => {
        store.setHideRecordMessage(this.userId, this.hideModal);
        this.setIsHideRecordMessage(this.hideModal);
        this.setRecording(!this.isRecording);

        const hideValue = this.hideModal ? 1 : 0;
        apolloProvider.defaultClient.mutate({
          mutation: SET_HIDE_MYSTAGE_RECORD_MESSAGE,
          variables: { value: hideValue }
        }).catch((error) => {
          this.$logger.error('[Recorder] Failed to persist hide record message', {
            operation: 'recorder',
            error: error.message
          });
        });
      })
      .catch(() => {
        return false;
      });
    },

    checkHideModal(e) {
      this.hideModal = e;
    },

    closedBrowser() {
      if (this.recorder && this.isRecording) {
        this.$logger.info('Browser/page is closed or suddenly refreshed', {
          operation: 'recorder',
          rec_status: 'closed'
        });
        this.stopRecorder();
      }
    },

    getUserAudio(stream) {
      let audioStream = new MediaStream();
      stream.getAudioTracks().forEach(track => {
        audioStream.addTrack(track);
      });
      return audioStream;
    },

    async handleRecording(status) {
      if (status) {
        if (!this.recorder) {
          await this.startRecording();
        }
      } else {
        this.stopRecorder();
        if (this.recordSuccess) {
          this.$logger.info('Recording completed.', {
            operation: 'recorder',
            rec_status: 'complete',
            err: 1,
          });
        }
      }
    },

    async startRecording() {
      let vm = this;
      await navigator.mediaDevices
        .getDisplayMedia({
          video: {
            frameRate: 60
          },
        })
        .then((stream) => {
          vm.setDisplayStream(stream);
          stream.getVideoTracks()[0].onended = function () {
            vm.stopRecorder();
          };
          let streams = [stream, vm.getUserAudio(vm.localUser.stream)];
          if (vm.remoteUsers.length > 0) {
            streams.push(this.getUserAudio(vm.remoteUsers[0].stream));
          }
          const options = {
            mimeType: 'video/webm; codecs=vp9',
            videoBitsPerSecond: 15000000,
          };
          vm.initRecorder(streams, options);
          vm.setRecording(true);
        })
        .catch((e) => {
          vm.setRecording(false);
          vm.recordSuccess = false;

          if (e.message === 'Permission denied') {
            vm.$logger.warn('Share screen permission denied', {
              operation: 'recorder',
              rec_status: 'denied',
            });
          } else {
            vm.$logger.error('Something went wrong. Recorder stopped.', {
              operation: 'recorder',
              rec_status: 'error',
              error: e.message,
              event_type: 7,
              lesson_id: this.lessonId
            });
          }
        });
    },

    async showNotification() {
      this.$notify({
        title: 'Notice',
        message: this.$t(`screenRecordNotification.${this.accType}`),
        type: 'info',
        position: 'bottom-right',
        duration: 3000
      });
    },
  },
  watch: {
    isRecording: {
      handler: 'handleRecording',
    },
    localUser: {
      handler: function(newVal) {
        if (this.isRecording && this.recorder) {
          let audioStreams = [this.getUserAudio(newVal.stream)];
          if (this.remoteUsers.length > 0) {
            audioStreams.push(this.getUserAudio(this.remoteUsers[0].stream));
          }
          this.updateAudioStreams(audioStreams);
        }
      },
      deep: true,
    },
    remoteUsers: {
      handler: async function (newVal) {
        if (newVal.length > 0 && this.isRecording && this.recorder && !this.isFirefoxBrowser) {
          this.stopRecorder();
          await this.showNotification();
          await this.startRecording();
        }
      },
      deep: true
    }
  },
  beforeDestroy: function() {
    window.removeEventListener('beforeunload', this.closedBrowser);
  }
};
</script>

<style scoped src="@/assets/css/pc/recorder.css"></style>
